import { useState, useEffect, useRef } from 'react';
import { SearchClientType, SearchResultItem } from '../types';
import { multiSearchAlgolia } from '../utils/search-execution';
import {
   allDoctypes,
   createSearchParameters,
   type Doctype,
   getFiltersForDoctype,
   orderedDoctypes,
} from '@ifixit/helpers';
import { SearchRequest } from '../utils/search-execution';

interface UseInstantSearchProps {
   searchClient: SearchClientType;
   indices: Partial<Record<Doctype, string[]>>;
   isProUser: boolean;
   langid?: string;
   storeCode?: string;
   allStoreCodes?: string[];
}

/**
 * Custom hook to handle the instant search logic
 * This separates the search functionality from UI rendering
 */
export function useInstantSearch({
   searchClient,
   indices,
   isProUser,
   langid = 'en',
   storeCode,
   allStoreCodes,
}: UseInstantSearchProps) {
   const [query, setQuery] = useState('');
   const [isSearching, setIsSearching] = useState(false);
   const [results, setResults] = useState<Record<Doctype, SearchResultItem[]>>(
      {} as Record<Doctype, SearchResultItem[]>
   );
   const [totalHits, setTotalHits] = useState<Record<Doctype, number>>(
      {} as Record<Doctype, number>
   );
   const [hasSearched, setHasSearched] = useState(false);
   const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

   useEffect(() => {
      const handleSearch = async () => {
         if (!query.trim() || !searchClient) {
            clearSearchState(setResults, setTotalHits, setIsSearching);
            setHasSearched(false);
            return;
         }

         setIsSearching(true);

         try {
            // Execute the search operation
            const { searchResults, hitCounts } = await executeSearchOperation(
               query,
               searchClient,
               indices,
               isProUser,
               langid,
               storeCode,
               allStoreCodes
            );

            setResults(searchResults);
            setTotalHits(hitCounts);
            setHasSearched(true);
         } catch (error) {
            console.error('Search error:', error);
         } finally {
            setIsSearching(false);
         }
      };

      if (searchTimeoutRef.current) {
         clearTimeout(searchTimeoutRef.current);
      }

      if (query.trim()) {
         searchTimeoutRef.current = setTimeout(handleSearch, 150);
      } else {
         clearSearchState(setResults, setTotalHits, setIsSearching);
         setHasSearched(false);
      }

      return () => {
         if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
         }
      };
   }, [query, searchClient, indices, langid, isProUser, storeCode, allStoreCodes]);

   const hasResults = (Object.values(results) as SearchResultItem[][]).some(
      (hits: SearchResultItem[]) => hits.length > 0
   );

   return {
      query,
      setQuery,
      isSearching,
      results,
      totalHits,
      hasResults,
      hasSearched,
   };
}

/**
 * Clears search state when there's no query or search client
 */
function clearSearchState(
   setResults: (value: Record<Doctype, SearchResultItem[]>) => void,
   setTotalHits: (value: Record<Doctype, number>) => void,
   setIsSearching: (value: boolean) => void
) {
   // Initialize empty state with all doctypes
   const emptyResults: Record<Doctype, SearchResultItem[]> = {} as Record<
      Doctype,
      SearchResultItem[]
   >;
   const emptyHits: Record<Doctype, number> = {} as Record<Doctype, number>;

   allDoctypes.forEach(doctype => {
      emptyResults[doctype] = [];
      emptyHits[doctype] = 0;
   });

   setResults(emptyResults);
   setTotalHits(emptyHits);
   setIsSearching(false);
}

/**
 * Creates search requests for all document types and returns one promise
 */
function createSearchRequests(
   indices: Partial<Record<Doctype, string[]>>,
   query: string,
   isProUser: boolean,
   langid: string,
   storeCode?: string,
   allStoreCodes?: string[]
): {
   requestsByDoctype: Record<string, { doctype: Doctype; indexName: string }>;
   requests: SearchRequest[];
} {
   const requests: SearchRequest[] = [];
   const requestsByDoctype: Record<string, { doctype: Doctype; indexName: string }> = {};

   for (const entry of Object.entries(indices) as [Doctype, string[] | undefined][]) {
      const [doctype, indexNames] = entry;
      if (!indexNames || indexNames.length === 0) continue;

      const filters = getFiltersForDoctype({
         doctype,
         isProUser,
         langid,
         storeCode,
         allStoreCodes,
      });
      const searchParameters = createSearchParameters(doctype);

      // Create requests for each index of this doctype
      for (const indexName of indexNames) {
         const requestKey = `${doctype}_${requests.length}`;
         requestsByDoctype[requestKey] = { doctype, indexName };

         requests.push({
            indexName,
            query,
            params: {
               hitsPerPage: 3,
               snippetEllipsisText: '...',
               ...(filters ? { filters } : {}),
               ...searchParameters,
            },
         });
      }
   }

   return { requestsByDoctype, requests };
}

/**
 * Processes search results and maps them back to doctypes
 */
function processSearchResults(
   searchResults: Array<{ hits: SearchResultItem[]; nbHits: number; queryID: string }>,
   requestsByDoctype: Record<string, { doctype: Doctype; indexName: string }>
): { searchResults: Record<Doctype, SearchResultItem[]>; hitCounts: Record<Doctype, number> } {
   const hitCounts: Record<Doctype, number> = {} as Record<Doctype, number>;

   const orderedResultsMap = getOrderedResults(orderedDoctypes);

   // Process results by matching them back to their doctypes using request order
   const requestKeys = Object.keys(requestsByDoctype);
   searchResults.forEach((result, index) => {
      const requestKey = requestKeys[index];
      if (!requestKey) return;

      const { doctype, indexName } = requestsByDoctype[requestKey];
      const { hits, nbHits } = result;

      if (hits.length > 0) {
         hits.forEach(hit => {
            hit.doctype ||= doctype;
            hit._queryID = result.queryID;
            hit._index = indexName;
         });
         // Combine results for the same doctype (in case of multiple indices)
         orderedResultsMap[doctype] = [...(orderedResultsMap[doctype] || []), ...hits];
         hitCounts[doctype] = (hitCounts[doctype] || 0) + nbHits;
      }
   });

   return { searchResults: orderedResultsMap, hitCounts };
}

/**
 * Executes the actual search operation
 */
async function executeSearchOperation(
   query: string,
   searchClient: SearchClientType,
   indices: Partial<Record<Doctype, string[]>>,
   isProUser: boolean,
   langid: string,
   storeCode?: string,
   allStoreCodes?: string[]
): Promise<{
   searchResults: Record<Doctype, SearchResultItem[]>;
   hitCounts: Record<Doctype, number>;
}> {
   // Create search requests for all doctypes
   const { requestsByDoctype, requests } = createSearchRequests(
      indices,
      query,
      isProUser,
      langid,
      storeCode,
      allStoreCodes
   );

   try {
      // Execute the batch search
      const searchResults = await multiSearchAlgolia<SearchResultItem>(searchClient, requests);

      // Process and map results back to doctypes
      return processSearchResults(searchResults, requestsByDoctype);
   } catch (error) {
      console.error('Search operation failed:', error);
      // Return empty results for all doctypes
      const emptyResults: Record<Doctype, SearchResultItem[]> = {} as Record<
         Doctype,
         SearchResultItem[]
      >;
      const emptyHits: Record<Doctype, number> = {} as Record<Doctype, number>;

      allDoctypes.forEach(doctype => {
         emptyResults[doctype] = [];
         emptyHits[doctype] = 0;
      });

      return { searchResults: emptyResults, hitCounts: emptyHits };
   }
}

/**
 * Define the preferred order of doctypes for search results
 */
function getOrderedResults(orderedDoctypes: Doctype[]): Record<Doctype, SearchResultItem[]> {
   // Map the ordered results back to their original search results
   const orderedResultsMap: Record<Doctype, SearchResultItem[]> = {} as Record<
      Doctype,
      SearchResultItem[]
   >;
   orderedDoctypes.forEach(doctype => {
      orderedResultsMap[doctype] = [];
   });

   return orderedResultsMap;
}
